import {Button, DialogActions, DialogContent, DialogTitle, IconButton, Radio} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import {useAsync2, useAsyncAction} from "nate-react-api-helpers";
import {api} from "../../../../api/API";
import {useEffect, useRef, useState} from "react";
import {ManageAlternativeAddress} from "./ManageAlternativeAddress";
import {AltAddress} from "../../../../api/Projects";
import EditIcon from "@mui/icons-material/Edit";
import {useSyncedRef} from "../../../../misc/SyncedRef";

export function ManageAlternativeAddresses(props: {
    project: number;
    onClose(): void;

    initialId?: number;
    initialContractorId?: number;
    includeContractor?: boolean
    onSelect?(value: AltAddress | null): Promise<any>
}) {
    const list = useAsync2((input) => api.projects.listAltAddresses(input), {
        project: props.project,
        excludeContractor: !props.includeContractor,
    }, [props.project, props.includeContractor])

    const [showNew, setShowNew] = useState(false);
    const [showEdit, setShowEdit] = useState<AltAddress|null>(null);
    const [selected, setSelected] = useState<AltAddress>();

    const fullList = list.asList
    const {initialId, initialContractorId} = props;
    const listLoaded = !list.loadingOrError;
    const doneInitial = useRef(false);

    useEffect(() => {
        if(!listLoaded) return;

        if(doneInitial.current) return;
        doneInitial.current = true;

        if(initialId) {
            setSelected(fullList.find(v => v.id === initialId));
        } else if(initialContractorId) {
            setSelected(fullList.find(v => v.contractorId === initialContractorId));
        } else {
            setSelected(undefined)
        }
    }, [fullList, initialId, initialContractorId, listLoaded]);

    const isFirstListLoad = useRef(true);

    useEffect(() => {
        if(listLoaded && isFirstListLoad.current) {
            isFirstListLoad.current = false;

            if (fullList.length === 0) {
                setShowNew(true);
            }
        }
    }, [fullList, listLoaded]);

    const hasSelection = !!props.onSelect

    const onSelectRef = useSyncedRef(props.onSelect);
    const onCloseRef = useSyncedRef(props.onClose);
    const update = useAsyncAction(async (input) => {
        if(onSelectRef.current) {
            await onSelectRef.current(input)
        }

        onCloseRef.current()
    }, []);

    if(showNew) {
        return <ManageAlternativeAddress project={props.project} onDone={(value: AltAddress | undefined) => {
            setShowNew(false);
            list.reload();
            setSelected(value)
        }} />
    }

    if(showEdit) {
        return <ManageAlternativeAddress value={showEdit} project={props.project} onDone={(value: AltAddress | undefined) => {
            setShowEdit(null);
            list.reload();
            setSelected(value)
        }} />
    }

    return (
        <Dialog2 open onClose={() => props.onClose()}>
            <DialogTitle>
                Alternative Addresses
            </DialogTitle>
            <DialogContent>
                {list.LoadingOrErrorElement}
                {list.asList?.map((address, i) => (
                    <div key={address.id} style={{
                        padding: 0,
                        paddingTop: 8,
                        paddingBottom: 8,
                        display: "flex",
                        alignItems: "center"
                    }}>
                        {hasSelection && <div>
                            <Radio checked={!!selected && selected.id === address.id ? true :
                                !!selected && selected.contractorId ? selected.contractorId === address.contractorId : false}
                               onChange={(_, checked) => {
                                   if(checked) {
                                       setSelected(address);
                                   } else {
                                       setSelected(undefined);
                                   }
                               }}
                            />
                        </div>}
                        <div style={{flex: 1}}>
                            <div style={{fontSize: "0.75rem", fontWeight: "300"}}>{address.name}</div>
                            <div style={{fontSize: "0.9rem"}}>
                                <div>{address.addressStreet}</div>
                                <div>{address.addressCity}, {address.addressProvince}</div>
                                <div>{address.addressPostal}</div>
                            </div>
                        </div>
                        {!address.contractorId && <div>
                            <IconButton onClick={() => setShowEdit(address)}>
                                <EditIcon />
                            </IconButton>
                        </div>}
                    </div>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowNew(true)}>New Address</Button>
                {hasSelection && <Button onClick={() => update.callback(null)}>Clear Selection</Button>}

                {update.LoadingElement}
                <div style={{flex: 1}} />
                {hasSelection ? <>
                    <Button onClick={() => props.onClose()}>Cancel</Button>
                    <Button variant="contained" onClick={() => update.callback(selected)}>Select</Button>
                </> :
                    <Button onClick={() => props.onClose()}>Close</Button>
                }
            </DialogActions>
        </Dialog2>
    )
}